import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { ApiResponse } from '../../interfaces/api-response';
import { WalleyComponent } from '../walley/walley.component';
import { AssistantChatResponse, AssistantStore } from './assistant.store';
import { AssistantConfigService, AssistantDockPosition } from '../../services/assistant-config.service';

@Component({
  selector: 'app-assistant-panel',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, MatTooltipModule, WalleyComponent, RouterLink],
  templateUrl: './assistant-panel.component.html',
  styleUrl: './assistant-panel.component.scss',
})
export class AssistantPanelComponent {
  private readonly assistantConfig = inject(AssistantConfigService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly store = inject(AssistantStore);
  @ViewChild('dock') private dockRef?: ElementRef<HTMLElement>;

  readonly messageControl = new FormControl<string>(this.store.draft, { nonNullable: true });
  private readonly suggestionKeys: Record<string, string> = {
    weco_tickets: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_SUGGEST_TODAY',
    systems: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_SUGGEST_TECH',
    orders: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_SUGGEST_ORDERS',
    tickets: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_SUGGEST_LEGACY',
    vouchers: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_SUGGEST_VOUCHERS',
  };
  topics: string[] = [];

  visible = false;
  /** Lato a cui è agganciato il dock. Gli offset `null` usano il margine di base. */
  anchorX: 'left' | 'right' = 'left';
  anchorY: 'top' | 'bottom' = 'bottom';
  offsetX: number | null = null;
  offsetY: number | null = null;
  dragging = false;
  private suppressOpen = false;
  private dragged = false;
  private appliedPosition: AssistantDockPosition | null = null;
  headerHidden = false;
  private headerReady = false;

  constructor() {
    this.store.language = (this.translate.currentLang as 'it' | 'en') || this.store.language;
    this.messageControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.store.draft = value;
      });

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.lang === 'it' || event.lang === 'en') {
          this.store.language = event.lang;
        }
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.refreshVisibility());

    this.assistantConfig.savedPreference$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((config) => {
        this.applyHeaderPreference(config.header_hidden === 1);
        this.applySavedPosition(config.position);
      });

    this.store.headerDock$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shown) => {
        if (shown && this.appliedPosition) {
          this.dragged = false;
          this.applySavedPosition(this.appliedPosition);
        }
      });

    this.refreshVisibility();
  }

  openCompact(): void {
    if (this.suppressOpen) {
      this.suppressOpen = false;
      return;
    }
    this.placeWindow();
    this.fitWindow();
    this.store.mode = this.store.messages.length > 0 ? 'expanded' : 'compact';
  }

  startDrag(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const handle = event.currentTarget as HTMLElement;
    const dock = handle.closest('.assistant-dock') as HTMLElement | null;
    if (!dock) {
      return;
    }

    const rect = dock.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    const startLeft = rect.left;
    const startTop = rect.top;
    let moved = false;
    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      // Il puntatore può essere già rilasciato: i listener sulla finestra bastano.
    }

    const move = (pointerEvent: PointerEvent) => {
      const dx = pointerEvent.clientX - startX;
      const dy = pointerEvent.clientY - startY;
      if (!moved && Math.hypot(dx, dy) < 6) {
        return;
      }
      moved = true;
      this.dragging = true;
      this.dragged = true;
      pointerEvent.preventDefault();
      const margin = 8;
      const minTop = this.headerClearance();
      const maxLeft = Math.max(margin, window.innerWidth - rect.width - margin);
      const maxTop = Math.max(minTop, window.innerHeight - rect.height - margin);
      this.anchorX = 'left';
      this.anchorY = 'top';
      this.offsetX = Math.round(Math.min(maxLeft, Math.max(margin, startLeft + dx)));
      this.offsetY = Math.round(Math.min(maxTop, Math.max(minTop, startTop + dy)));
    };
    const end = (pointerEvent: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      if (handle.hasPointerCapture(pointerEvent.pointerId)) {
        handle.releasePointerCapture(pointerEvent.pointerId);
      }
      this.dragging = false;
      if (moved) {
        this.suppressOpen = true;
        setTimeout(() => {
          this.suppressOpen = false;
        });
      }
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }

  close(): void {
    this.store.mode = 'closed';
  }

  startResize(event: PointerEvent): void {
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    const win = handle.parentElement?.querySelector('.assistant-window') as HTMLElement | null;
    if (!win) {
      return;
    }

    const rect = win.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    const startW = rect.width;
    const startH = rect.height;
    handle.setPointerCapture(event.pointerId);

    const move = (pointerEvent: PointerEvent) => {
      const maxW = Math.max(320, window.innerWidth - 32);
      const maxH = Math.max(280, window.innerHeight - 32);
      this.store.width = Math.round(
        Math.min(maxW, Math.max(320, startW - (pointerEvent.clientX - startX))),
      );
      this.store.height = Math.round(
        Math.min(maxH, Math.max(280, startH - (pointerEvent.clientY - startY))),
      );
    };
    const end = (pointerEvent: PointerEvent) => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      if (handle.hasPointerCapture(pointerEvent.pointerId)) {
        handle.releasePointerCapture(pointerEvent.pointerId);
      }
    };

    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  replyParagraphs(text: string): Array<Array<Array<{ text: string; bold: boolean }>>> {
    const cleaned = text
      .replace(/\r/g, '')
      .split('\n')
      .filter((line) => !this.isMarkdownTableLine(line))
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s*JSON finale\s*$/i, '')
      .trim();

    if (!cleaned) {
      return [];
    }

    return cleaned.split(/\n\s*\n/).map((paragraph) =>
      paragraph.split('\n').map((line) => this.splitBold(line)),
    );
  }

  columnLabel(column: string): string {
    const keys: Record<string, string> = {
      code: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_CODE',
      status_name_it: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_STATUS',
      status_name_en: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_STATUS',
      substatus_name_it: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_SUBSTATUS',
      substatus_name_en: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_SUBSTATUS',
      days_open: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_DAYS',
      reason: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_REASON',
      incharge_nickname: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_TECH',
      nickname: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_TECH',
      taken_in_charge: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.KPI_TAKEN',
      closed_in_period: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.KPI_CLOSED',
      open_in_charge: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.KPI_BACKLOG',
      system_name: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_SYSTEM',
      product_type: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_TYPE',
      owner_nickname: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_OWNER',
      open_tickets: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_OPEN_TICKETS',
      ticket_codes: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_TICKETS',
      progressivo: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_ORDER',
      order_progressivo: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_ORDER',
      status_name: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_STATUS',
      created_on: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_DATE',
      count: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.COL_COUNT',
    };
    return keys[column] ?? column;
  }

  cellParts(column: string, row: Record<string, unknown>): Array<{ text: string; route: string[] | null; label: string | null }> {
    const text = this.cellValue(column, row[column]);
    const entry = this.linkEntry(row, column);
    if (Array.isArray(entry)) {
      const labels = text === '—' ? [] : text.split(/\s*,\s*/).filter((label) => label !== '');
      if (labels.length === 0) {
        return [{ text, route: null, label: null }];
      }
      const byLabel = new Map(
        entry
          .filter((item) => item.label !== '')
          .map((item) => [item.label.toLowerCase(), item]),
      );
      return labels.map((label) => {
        const link = byLabel.get(label.toLowerCase()) ?? null;
        return {
          text: label,
          route: link ? this.routeFor(link) : null,
          label: link ? this.linkLabel(link.kind) : null,
        };
      });
    }

    const route = entry ? this.routeFor(entry) : null;
    return [{
      text,
      route,
      label: entry && route ? this.linkLabel(entry.kind) : null,
    }];
  }

  cellValue(column: string, value: unknown): string {
    if (value == null || value === '') {
      return '—';
    }
    const text = String(value);
    if (column === 'reason') {
      const known = ['unassigned', 'still_open', 'stale', 'pending'];
      if (known.includes(text)) {
        return this.translate.instant(`SYSTEM.SYSTEM.DASHBOARD_TICKET.REASON.${text}`);
      }
    }
    return text;
  }

  private linkEntry(
    row: Record<string, unknown>,
    column: string,
  ): { kind: string; id: number; label?: string } | Array<{ kind: string; id: number; label: string }> | null {
    const links = row['links'];
    if (!links || typeof links !== 'object' || Array.isArray(links)) {
      return null;
    }
    const entry = (links as Record<string, unknown>)[column];
    if (Array.isArray(entry)) {
      return entry.flatMap((item) => {
        const link = this.asLink(item);
        return link?.label ? [link] : [];
      });
    }
    return this.asLink(entry);
  }

  private asLink(value: unknown): { kind: string; id: number; label: string } | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }
    const kind = (value as { kind?: unknown }).kind;
    const id = (value as { id?: unknown }).id;
    const label = (value as { label?: unknown }).label;
    if ((kind !== 'ticket' && kind !== 'system' && kind !== 'order' && kind !== 'legacy_ticket' && kind !== 'voucher') || typeof id !== 'number' || id <= 0) {
      return null;
    }
    return { kind, id, label: typeof label === 'string' ? label : '' };
  }

  private routeFor(link: { kind: string; id: number }): string[] | null {
    if (link.kind === 'ticket') {
      return ['/systems/ticket', String(link.id)];
    }
    if (link.kind === 'system') {
      return ['/systems/systemReview', String(link.id)];
    }
    if (link.kind === 'order') {
      return ['/lavorazioni/commesse/gestioneDettagli', String(link.id)];
    }
    if (link.kind === 'legacy_ticket') {
      return ['/ticket/ticketDetail', String(link.id)];
    }
    if (link.kind === 'voucher') {
      return ['/voucher/voucherEdit', String(link.id)];
    }
    return null;
  }

  private linkLabel(kind: string): string {
    const keys: Record<string, string> = {
      ticket: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_OPEN_TICKET',
      system: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_OPEN_SYSTEM',
      order: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_OPEN_ORDER',
      legacy_ticket: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_OPEN_LEGACY_TICKET',
      voucher: 'SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_OPEN_VOUCHER',
    };
    const key = keys[kind];
    return key ? this.translate.instant(key) : '';
  }

  private isMarkdownTableLine(line: string): boolean {
    const trimmed = line.trim();
    if (trimmed.split('|').length >= 3) {
      return true;
    }
    return /^[-:|\s]{3,}$/.test(trimmed);
  }

  private splitBold(line: string): Array<{ text: string; bold: boolean }> {
    const parts: Array<{ text: string; bold: boolean }> = [];
    const pattern = /\*\*(.+?)\*\*/g;
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(line)) !== null) {
      if (match.index > last) {
        parts.push({ text: line.slice(last, match.index), bold: false });
      }
      parts.push({ text: match[1], bold: true });
      last = match.index + match[0].length;
    }
    if (last < line.length) {
      parts.push({ text: line.slice(last), bold: false });
    }
    return parts.length > 0 ? parts : [{ text: line, bold: false }];
  }

  sendSuggestion(key: string): void {
    const translated = this.translate.instant(key);
    this.messageControl.setValue(translated && translated !== key ? translated : key);
    this.send();
  }

  send(): void {
    const message = this.messageControl.value.trim();
    if (!message || this.store.loading) {
      return;
    }

    this.store.error = null;
    this.store.messages = [...this.store.messages, { role: 'user', text: message }];
    this.messageControl.setValue('');
    this.store.loading = true;
    this.store.mode = 'expanded';

    this.assistantConfig
      .ask({
        message,
        language: this.store.language,
        period: this.store.period,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ApiResponse<AssistantChatResponse>) => {
          this.store.loading = false;
          const data = response?.data;
          if (!data) {
            this.store.error = 'AI response empty';
            return;
          }

          this.store.messages = [
            ...this.store.messages,
            {
              role: 'assistant',
              text: data.reply,
              table: data.table,
            },
          ];
          this.store.lastSources = data.sources ?? [];
          this.store.mode = 'expanded';

          if (data.view_updates) {
            this.store.publishViewUpdate(data.view_updates);
          }
        },
        error: () => {
          this.store.loading = false;
          this.store.error = 'AI assistant unavailable';
          this.store.mode = 'expanded';
        },
      });
  }

  get suggestions(): string[] {
    const keys = this.topics
      .map((topic) => this.suggestionKeys[topic])
      .filter((key): key is string => !!key);
    return keys.slice(0, 3);
  }

  subtitle(): string {
    const labels = this.topics
      .map((topic) => this.translate.instant('SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_TOPIC_' + topic.toUpperCase()))
      .filter((label) => typeof label === 'string' && label !== '');
    if (labels.length === 0) {
      return this.translate.instant('SYSTEM.SYSTEM.DASHBOARD_TICKET.AI_SUBTITLE');
    }
    return labels.join(' · ');
  }

  private applySavedPosition(position: AssistantDockPosition): void {
    if (this.dragged && this.appliedPosition === position) {
      return;
    }
    this.dragged = false;
    this.appliedPosition = position;
    this.offsetX = null;
    this.offsetY = null;
    if (position === 'bottom_right') {
      this.anchorX = 'right';
      this.anchorY = 'bottom';
      return;
    }
    if (position === 'top_left') {
      this.anchorX = 'left';
      this.anchorY = 'top';
      return;
    }
    if (position === 'top_right') {
      this.anchorX = 'right';
      this.anchorY = 'top';
      return;
    }
    this.anchorX = 'left';
    this.anchorY = 'bottom';
  }

  /** Tiene la finestra alla misura standard, facendola crescere verso lo spazio libero. */
  private placeWindow(): void {
    const dock = this.dockRef?.nativeElement;
    if (!dock) {
      return;
    }
    const rect = dock.getBoundingClientRect();
    const margin = 8;
    const width = Math.min(this.targetWidth(), window.innerWidth - margin * 2);
    const height = Math.min(this.targetHeight(), window.innerHeight - margin * 2);
    const pinRight = rect.left + width > window.innerWidth - margin;
    const pinBottom = rect.top + height > window.innerHeight - margin;

    if (pinRight) {
      const maxRight = Math.max(margin, window.innerWidth - width - margin);
      this.anchorX = 'right';
      this.offsetX = Math.round(Math.min(maxRight, Math.max(margin, window.innerWidth - rect.right)));
    } else {
      const maxLeft = Math.max(margin, window.innerWidth - width - margin);
      this.anchorX = 'left';
      this.offsetX = Math.round(Math.min(maxLeft, Math.max(margin, rect.left)));
    }

    if (pinBottom) {
      const maxBottom = Math.max(margin, window.innerHeight - height - margin);
      this.anchorY = 'bottom';
      this.offsetY = Math.round(Math.min(maxBottom, Math.max(margin, window.innerHeight - rect.bottom)));
    } else {
      const minTop = this.headerClearance();
      const maxTop = Math.max(minTop, window.innerHeight - height - margin);
      this.anchorY = 'top';
      this.offsetY = Math.round(Math.min(maxTop, Math.max(minTop, rect.top)));
    }
  }

  private headerClearance(): number {
    const bar = document.querySelector('.app-topbar');
    const height = bar?.getBoundingClientRect().height ?? 64;
    return Math.ceil(height) + 8;
  }

  private applyHeaderPreference(hidden: boolean): void {
    if (this.headerReady && hidden && !this.headerHidden) {
      this.store.shownFromHeader = false;
      this.store.mode = 'closed';
    }
    this.headerHidden = hidden;
    this.headerReady = true;
  }

  private targetWidth(): number {
    if (this.store.width != null) {
      return this.store.width;
    }
    return this.store.messages.length > 0 ? 576 : 384;
  }

  private targetHeight(): number {
    if (this.store.height != null) {
      return this.store.height;
    }
    return this.store.messages.length > 0 ? 480 : 280;
  }

  private fitWindow(): void {
    const maxH = Math.max(280, window.innerHeight - 32);
    const maxW = Math.max(320, window.innerWidth - 32);
    if (this.store.height != null && this.store.height > maxH) {
      this.store.height = Math.round(maxH);
    }
    if (this.store.width != null && this.store.width > maxW) {
      this.store.width = Math.round(maxW);
    }
  }

  private refreshVisibility(): void {
    const path = this.router.url.split('?')[0].split('#')[0];
    if (path === '/login' || path.startsWith('/login/')) {
      this.store.clearConversation();
      this.visible = false;
      this.topics = [];
      return;
    }
    this.assistantConfig.getConfig().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (config) => {
        this.topics = config.topics;
        this.visible = config.visible === 1;
        this.applyHeaderPreference(config.header_hidden === 1);
        this.applySavedPosition(config.position);
      },
      error: () => {
        this.visible = false;
        this.topics = [];
      },
    });
  }
}
